import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  createContext,
} from 'react';
// import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  useCancelableBookableQuery,
  useLazyUpcomingBookablesQuery,
  useLazyActiveProductsQuery,
  useLazySpecificBookablesQuery,
  useReserveAppoinmentMutation,
  useRevokeAppointmentReservationMutation,
} from '@timum/timum_pdk';

import { isNil, isEqual } from 'lodash';
import {
  selectSelectedProduct,
  selectPublicData,
  selectAppConfigProp,
  selectProduct,
  mutateAppConfig,
  selectSelectedResource,
  selectResToPublicDataMap,
  selectSelectedPublicData,
  selectIsIdentifyingCustomer,
  selectCustomerData,
  selectCustomerIdentificationFailed,
  selectIsWaitingForConf,
} from '../slices/appState';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import useWindowDimensions from '../hooks/useWindowDimensions';

import Footer from './Footer';

import { DateTime } from 'luxon';

import ResourceSelector, {
  isHiddenForSelectedProduct,
} from './ResourceSelector';
import { useTranslation } from 'react-i18next';

export const ProductPageContext = createContext();
export const CustomerFormContext = createContext();
export const CancelPageContext = createContext();
export const ConfirmationPageContext = createContext();
export const CalendarViewContext = createContext();
export const LoginPageContext = createContext();
export const ErrorContext = createContext();

const calendarActions = {
  setBookables: 'setBookables',
  setProducts: 'setProducts',
  resetProducts: 'resetProducts',
};

function CalendarReducer(state, { type, payload }) {
  switch (type) {
    case calendarActions.setBookables: {
      return { ...state, bookables: payload };
    }
    case calendarActions.setProducts: {
      return { ...state, products: payload };
    }
    case calendarActions.resetProducts: {
      return { ...state, products: undefined };
    }
    default: {
      throw new Error(`Unsupported type: ${type}`);
    }
  }
}

export default function Calendar({
  children,
  reducer = CalendarReducer,
  products: controlledProducts,
  forceResourceSelectorDialog,
  suppressResourcePage = false,
}) {
  /***********************
   * STATE
   ***********************/
  const { i18n } = useTranslation();
  const { current: initialState } = React.useRef({
    bookables: undefined,
    products: undefined,
  });
  const [state, dispatchReducer] = React.useReducer(reducer, initialState);
  const dispatch = useDispatch();
  // The object/array-valued config selectors below pass `isEqual` as the
  // equality fn so their reference stays stable across a config `rerender` that
  // doesn't change their value. Without it, every rerender's fresh
  // `merge({}, primary, given)` deep-clone hands them a new reference, which
  // churns the identity of the fetch-trigger callbacks (fetchBookables*,
  // fetchProducts) and refires their effects — an unnecessary refetch. A real
  // value change still flips `isEqual` and refetches as before.
  const refPool = useSelector(
    (state) => selectAppConfigProp(state, 'ref'),
    isEqual,
  );
  const tslRefPool = useSelector(
    (state) => selectAppConfigProp(state, 'tslRefs'),
    isEqual,
  );
  const virtualTslRefPool = useSelector(
    (state) => selectAppConfigProp(state, 'virtualTslRefs'),
    isEqual,
  );
  // needed otherwise active_products is triggered needlessly as specific_prducts did not yet finish fetching.
  const prdRefPool = useSelector(
    (state) => selectAppConfigProp(state, 'prdRefs'),
    isEqual,
  );

  const givenTimeslots = useSelector(
    (state) => selectAppConfigProp(state, 'tsls'),
    isEqual,
  );
  const additionalData = useSelector(
    (state) => selectAppConfigProp(state, 'additionalData'),
    isEqual,
  );
  const product = useSelector(selectSelectedProduct);
  const resource = useSelector(selectSelectedResource);
  const publicData = useSelector(selectSelectedPublicData);
  const publicDataMap = useSelector(selectResToPublicDataMap);
  const customerData = useSelector(selectCustomerData);
  const isWaitingForConf = useSelector(selectIsWaitingForConf);
  const isIdentifyingCustomer = useSelector(selectIsIdentifyingCustomer);
  const identificationFailed = useSelector(selectCustomerIdentificationFailed);
  const showAllBookables = useMemo(() => {
    return publicData?.resource?.uuid === 'all-option';
  }, [publicData?.resource?.uuid]);
  const bookablesAreControlled =
    tslRefPool?.length > 0 ||
    virtualTslRefPool?.length > 0 ||
    givenTimeslots?.length > 0;
  const bookables = state.bookables;
  const productsAreControlled =
    controlledProducts != null || prdRefPool?.length > 0;
  const products = productsAreControlled ? controlledProducts : state.products;

  const { height: windowHeight } = useWindowDimensions();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const channelKey = useSelector((state) =>
    selectAppConfigProp(state, 'channelKey'),
  );

  const additionalParams = useSelector(
    (state) => selectAppConfigProp(state, 'additionalParams'),
    isEqual,
  );

  const cancelableAppointmentUuid = useSelector((state) =>
    selectAppConfigProp(state, 'cancelableAppointment.appointmentUuid'),
  );

  const defaultHeight = useSelector((state) =>
    selectAppConfigProp(state, 'height'),
  );

  const calendarFrontend = useSelector((state) =>
    selectAppConfigProp(state, 'calendarFrontend'),
  );

  const allowCloseOnBooking = useSelector((state) =>
    selectAppConfigProp(state, 'allowCloseOnBooking'),
  );

  const allowCloseOnCancel = useSelector((state) =>
    selectAppConfigProp(state, 'allowCloseOnCancel'),
  );

  const constrainDialogsToContainer = useSelector((state) =>
    selectAppConfigProp(state, 'constrainDialogsToContainer'),
  );

  const hideTimumFooter = useSelector((state) =>
    selectAppConfigProp(state, 'hideTimumFooter'),
  );

  const height =
    windowHeight && windowHeight < defaultHeight ? windowHeight : defaultHeight;

  const authToken = useSelector((state) =>
    selectAppConfigProp(state, 'cancelableAppointment.authToken'),
  );

  const fetchingProductsFailed = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingProductsFailed'),
  );

  const fetchingBookablesFailed = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingBookablesFailed'),
  );

  const fetchingCancelableBookableFailed = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingCancelableBookableFailed'),
  );

  const fetchingProductsSucceeded = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingProductsSucceeded'),
  );

  const fetchingBookablesSucceeded = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingBookablesSucceeded'),
  );

  const fetchingPublicDataSucceeded = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.fetchingPublicDataSucceeded'),
  );

  const openedProductSelection = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.openedProductSelection'),
  );

  const openedResourceSelection = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.openedResourceSelection'),
  );

  const openedBookingPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.openedBookingPage'),
  );

  const openedCancelPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.openedCancelPage'),
  );

  const openedConfirmationPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.openedConfirmationPage'),
  );

  const closedProductSelection = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.closedProductSelection'),
  );

  const closedResourceSelection = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.closedResourceSelection'),
  );

  const closedBookingPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.closedBookingPage'),
  );

  const closedCancelPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.closedCancelPage'),
  );

  const closedConfirmationPage = useSelector((state) =>
    selectAppConfigProp(state, 'callbacks.closedConfirmationPage'),
  );

  const [isProductSelectionOpen, setProductSelection] = React.useState(false);
  // set when the product dialog was forced open because the selected resource
  // doesn't offer the customer's product — drives the hint and blocks dismissal
  const [productMismatch, setProductMismatch] = React.useState(false);
  const [isBookingPageOpen, setBookingPageOpen] = React.useState(false);
  const [isCancelPageOpen, setCancelPageOpen] = React.useState(false);
  const [wasCancelPageOpen, setWasCancelPageOpen] = React.useState(false); //<- so we don't reopen the page if user closed it manually
  const [isErrorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [isLoginPageOpen, setLoginPageOpen] = React.useState(false);
  const [isResourceSelectionOpen, setResourceSelectionOpen] =
    React.useState(false);
  const [isConfirmationPageOpen, setConfirmationPageOpen] =
    React.useState(false);
  const [shouldRestoreAllView, setShouldRestoreAllView] = React.useState(false);

  const [clickedEvent, setClickedEvent] = React.useState();
  const [clickedCancelableEvent, setClickedCancelableEvent] = React.useState();
  const [loginPagePrefilledNamed, setLoginPagePrefilledName] = React.useState();

  // A booking in progress owns the product: the slot was picked for it, and the
  // reservation is keyed on product.uuid. Stacking itself is fine — the login
  // dialog legitimately opens over the booking form from onBookingFailed — but
  // reconciling the product would contradict the booking already under way.
  // clickedEvent matters on its own: handleCloseBookingPage clears it on a
  // 150ms timeout, so isBookingPageOpen alone would leave a window open.
  const bookingFlowActive =
    isBookingPageOpen ||
    isConfirmationPageOpen ||
    !!clickedEvent ||
    // the "all" view is about to be handed back, and that restore dispatches in
    // the same commit this effect would run in — reconciling now would judge
    // the held product against the resource we are already leaving
    shouldRestoreAllView;

  // used by ConfirmationPage
  const [bookedEvent, setBookedEvent] = React.useState();
  const [bookingResponse, setBookingResponse] = React.useState();
  const [inputData, setInputData] = React.useState();

  /* Interfaces */

  const ResourcePage = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.ResourcePage'),
  );

  /***********************
   * QUERIES
   ***********************/

  const [
    fetchSpecificBookables,
    {
      data: specificBookablesData,
      error: specificBookablesError,
      isFetching: isFetchingSpecificBookables,
      isLoading: isLoadingSpecificBookables,
      isUninitialized: isFetchingSpecificBookablesUninitialized,
    },
  ] = useLazySpecificBookablesQuery();

  const [
    reserveAppointment,
    { data: reservation, error: reservationFailed, reset: resetReservation },
  ] = useReserveAppoinmentMutation({
    fixedCacheKey: 'reserveAppointmentCache',
  });

  const [revokeReservation] = useRevokeAppointmentReservationMutation();

  const [
    fetchBookables,
    {
      data: bookablesData,
      error: bookablesError,
      isFetching: isFetchingBookables,
      isLoading: isLoadingBookables,
      isUninitialized: isFetchingBookablesUninitialized,
    },
  ] = useLazyUpcomingBookablesQuery();

  const [
    fetchProducts,
    {
      data: productsData,
      error: productsError,
      isFetching: isFetchingProducts,
    },
  ] = useLazyActiveProductsQuery();

  // Query for cancelable bookable - fires immediately when authToken is present
  const {
    data: cancelableBookableData,
    error: cancelableBookableError,
    isFetching: isFetchingCancelableBookable,
  } = useCancelableBookableQuery(
    {
      headers: { 'X-SIGNED-APP-CUSTOMER': authToken },
    },
    {
      skip: !authToken,
    },
  );

  /***********************
   * UTIL
   ***********************/

  /**
   * Removes the useless 'public_visible' prop.
   * Also parses start and end to luxon DateTimes without timezone information
   * Preserves original start and end values, storing them in untouchedStart and untouchedEnd respectively.
   * @param {} bookables
   */
  const preProcessBookables = useCallback(
    (bookables, product, locale, publicDataMap, skipSupportFilter) => {
      if (!bookables) return {};

      const parsedBookables = JSON.parse(JSON.stringify(bookables));
      delete parsedBookables.public_visible;

      for (const [date, timeslots] of Object.entries(parsedBookables)) {
        parsedBookables[date] = [];
        for (const timeslot of timeslots) {
          // An unstamped availability inherits its resource's default products,
          // and the "all resources" fetch cannot say whose defaults those are —
          // one prdRef, many refs. Supply the attribution the backend lost.
          // Explicitly shared timeslots are exempt: "if our customer wanted to
          // explicitly share the referenced appointments, it would be perceived
          // as a bug if they weren't displayed."
          if (
            !skipSupportFilter &&
            isHiddenForSelectedProduct(timeslot, product, publicDataMap)
          ) {
            continue;
          }

          parsedBookables[date].push(
            preProcessBookbale(timeslot, product, locale),
          );
        }
      }

      return parsedBookables;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Extracts the first cancelable bookable from a date-grouped bookables object.
   * Used by both the dedicated cancelable query and the fallback bookables logic.
   * @param {Object} dateGroupedBookables - Object with dates as keys and arrays of bookables as values
   * @param {string} [appointmentUuid] - Optional: filter by specific appointment UUID
   * @returns {Object|undefined} The first matching bookable, or undefined if none found
   */
  const findCancelableBookable = useCallback(
    (dateGroupedBookables, appointmentUuid) => {
      if (
        !dateGroupedBookables ||
        Object.keys(dateGroupedBookables).length === 0
      ) {
        return undefined;
      }

      for (const timeslots of Object.values(dateGroupedBookables)) {
        if (!Array.isArray(timeslots)) continue;

        if (appointmentUuid) {
          // Find specific appointment by UUID
          const match = timeslots.find(
            (tsl) => tsl.appointment_uuid === appointmentUuid,
          );
          if (match) return match;
        } else if (timeslots.length > 0) {
          // Return first bookable
          return timeslots[0];
        }
      }

      return undefined;
    },
    [],
  );

  // todo: instead of sending back untouchedEnd we instead want to adjust
  // the end based on the chosen product's min duration (if any) or the product's max duration as follows:
  // if product has no min/max duration -> use untouchedEnd
  // if product has minDuration -> add min duration to untouched start
  // if product has maxDuration -> add max duration to untouched start (bonus: check if untouchedEnd is before start + maxDuration and use untouchedEnd if so)
  // if product has both min and max -> add min duration to untouched start
  const preProcessBookbale = useCallback(
    (bookable, product, locale) => {
      const parsedBookable = JSON.parse(JSON.stringify(bookable));
      parsedBookable.isCancelable =
        cancelableAppointmentUuid &&
        parsedBookable.appointment_uuid === cancelableAppointmentUuid;

      parsedBookable.appointmentUuid = parsedBookable.appointment_uuid;
      parsedBookable.uuid = parsedBookable.timeslot_uuid;
      // Bind the display locale at creation. Luxon fixes locale when a DateTime
      // is made, so these must carry it explicitly — otherwise they freeze in
      // whatever Settings.defaultLocale was at creation time (which races i18n
      // init and loses), and every toLocaleString downstream renders English.
      parsedBookable.start = DateTime.fromISO(parsedBookable.start).setLocale(
        locale,
      );

      if (
        parsedBookable.kind?.toLowerCase().includes('bookable') && // only adjust for availabilities; appointments are fixed
        product?.minDuration
      ) {
        parsedBookable.end = parsedBookable.start.plus({
          minutes: product.minDuration,
        });
      } else if (
        parsedBookable.kind?.toLowerCase().includes('bookable') &&
        product?.maxDuration
      ) {
        parsedBookable.end = parsedBookable.start.plus({
          minutes: product.maxDuration,
        });
      } else {
        parsedBookable.end = DateTime.fromISO(parsedBookable.end).setLocale(
          locale,
        );
      }

      return parsedBookable;
    },
    [cancelableAppointmentUuid],
  );

  const fetchBookablesForTsl = useCallback(() => {
    const fetch = async () => {
      // this fetches data for multiple timeslots no matter to which resource thy belong...
      fetchSpecificBookables({
        params: {
          prdRef: product.uuid,
          channelKey: channelKey,
          tslIds: tslRefPool,
          ...additionalData,
        },
        body: virtualTslRefPool,
      });
    };

    if (
      product &&
      (tslRefPool?.length > 0 || virtualTslRefPool?.length > 0) &&
      !givenTimeslots?.length > 0
    ) {
      fetch();
    }
  }, [
    additionalData,
    channelKey,
    fetchSpecificBookables,
    givenTimeslots?.length,
    product,
    tslRefPool,
    virtualTslRefPool,
  ]);

  const fetchBookablesForSelectedRes = useCallback(() => {
    const fetch = async () => {
      fetchBookables({
        channelOrResourceId: publicData?.ref,
        params: {
          prdRef: product.uuid,
          channelKey: channelKey,
          ...additionalParams,
        },
        headers: authToken ? { 'X-SIGNED-APP-CUSTOMER': authToken } : undefined,
      });
    };

    if (!showAllBookables && !bookablesAreControlled && publicData && product) {
      fetch();
    }
  }, [
    additionalParams,
    authToken,
    bookablesAreControlled,
    channelKey,
    fetchBookables,
    product,
    publicData,
    showAllBookables,
  ]);

  const fetchBookablesForAllRes = useCallback(() => {
    const fetch = async () => {
      fetchBookables({
        channelOrResourceId: refPool[0],
        params: {
          ref: refPool.slice(1),
          prdRef: product.uuid,
          channelKey: channelKey,
          ...additionalParams,
        },
        headers: authToken ? { 'X-SIGNED-APP-CUSTOMER': authToken } : undefined,
      });
    };

    if (showAllBookables && !bookablesAreControlled && product) {
      fetch();
    }
  }, [
    additionalParams,
    authToken,
    bookablesAreControlled,
    channelKey,
    fetchBookables,
    product,
    refPool,
    showAllBookables,
  ]);

  /***********************
   * STATE MANAGEMENT
   ***********************/

  const handleOpenResourceSelectionPage = useCallback(() => {
    setResourceSelectionOpen(true);

    if (openedResourceSelection) {
      openedResourceSelection();
    }
  }, [openedResourceSelection]);

  const handleCloseResourceSelectionPage = () => {
    setResourceSelectionOpen(false);

    if (closedResourceSelection) {
      closedResourceSelection();
    }
  };

  const handleOpenProductPage = useCallback(() => {
    setProductSelection(true);

    if (openedProductSelection) {
      openedProductSelection();
    }
  }, [openedProductSelection]);

  const handleCloseProductPage = useCallback(() => {
    setProductSelection(false);
    setProductMismatch(false);

    if (closedProductSelection) {
      closedProductSelection();
    }
  }, [closedProductSelection]);

  const handleOpenLoginPage = (userName) => {
    setLoginPagePrefilledName(userName);
    setLoginPageOpen(true);
  };

  const handleCloseLoginPage = () => {
    setLoginPagePrefilledName(undefined);
    setLoginPageOpen(false);
  };

  const handleOpenBookingPage = useCallback(
    (event) => {
      setBookingPageOpen(true);
      setClickedEvent(event);

      if (showAllBookables || suppressResourcePage) {
        const data = Object.values(publicDataMap).find(
          (entry) => entry?.resource?.uuid === event.resource_uuid,
        );

        // Booking from the "all" view borrows the slot's resource; hand the
        // view back afterwards, otherwise the customer is left on a resource
        // that may not even offer the service they came in with.
        if (showAllBookables) {
          setShouldRestoreAllView(true);
        }

        // No match means we'd blank the selection entirely and reserve against
        // an undefined resource — leave the current one in place instead.
        if (data) {
          dispatch(selectPublicData(data));
        }
      }

      if (openedBookingPage) {
        openedBookingPage({ ...event });
      }
    },
    [
      dispatch,
      openedBookingPage,
      publicDataMap,
      showAllBookables,
      suppressResourcePage,
    ],
  );

  const handleCloseBookingPage = useCallback(() => {
    setBookingPageOpen(false);

    if (isNil(reservationFailed) && reservation) {
      revokeReservation({
        channelOrResourceId: publicData?.ref,
        body: {
          placeholder_id: reservation?.participation?.customer_uuid,
          appointment_id: reservation?.participation?.appointment_uuid,
        },
        params: {
          ...additionalParams,
        },
      });
    }

    if (closedBookingPage) {
      closedBookingPage({ ...clickedEvent });
    }

    // let the fade animation finish before setting this
    setTimeout(() => {
      setClickedEvent(undefined);
    }, 150);
  }, [
    additionalParams,
    clickedEvent,
    closedBookingPage,
    publicData?.ref,
    reservation,
    reservationFailed,
    revokeReservation,
  ]);

  const handleOpenConfirmationPage = useCallback(() => {
    setConfirmationPageOpen(true);

    if (openedConfirmationPage) {
      openedConfirmationPage();
    }
  }, [openedConfirmationPage]);

  const handleCloseConfirmationPage = useCallback(() => {
    setConfirmationPageOpen(false);
    setBookedEvent(undefined);
    setBookingResponse(undefined);
    setInputData(undefined);

    if (closedConfirmationPage) {
      closedConfirmationPage();
    }
  }, [closedConfirmationPage]);

  const handleOpenCancelPage = useCallback(
    (event) => {
      setCancelPageOpen(true);
      setClickedCancelableEvent(event);

      if (openedCancelPage) {
        openedCancelPage({ ...event });
      }
    },
    [openedCancelPage],
  );

  const handleCloseCancelPage = useCallback(() => {
    setCancelPageOpen(false);
    // let the fade animation finish before setting this
    setTimeout(() => {
      setClickedCancelableEvent(undefined);
    }, 150);

    if (closedCancelPage) {
      closedCancelPage({ ...clickedEvent });
    }
  }, [clickedEvent, closedCancelPage]);

  const handleCancelSuccess = useCallback(() => {
    dispatch(
      mutateAppConfig({
        pathToProp: 'cancelableAppointment',
        newValue: undefined,
      }),
    );
    handleCloseCancelPage();

    if (closedCancelPage) {
      closedCancelPage();
    }
  }, [closedCancelPage, dispatch, handleCloseCancelPage]);

  const handleOpenErrorDialog = () => {
    setErrorDialogOpen(true);
  };

  const handleCloseErrorDialog = useCallback(() => {
    setErrorDialogOpen(false);
    resetReservation(); // reset reservation so user may try a new bookable without issue
  }, [setErrorDialogOpen, resetReservation]);

  /***********************
   * USE MEMO / EFFECT
   ***********************/

  useEffect(() => {
    fetchBookablesForTsl();
  }, [fetchBookablesForTsl]);

  useEffect(() => {
    fetchBookablesForSelectedRes();
  }, [fetchBookablesForSelectedRes]);

  /**
   * Fetch bookables in the showAllBookables scenario
   * This doesn't trigger for publicData changes.
   */
  useEffect(() => {
    fetchBookablesForAllRes();
  }, [fetchBookablesForAllRes]);

  // we dispatch in a different useEffect so that the results of automatic refetches
  // can be properly dispatched. Otherwise, after the initial request, we'd never update the interface again
  useEffect(() => {
    if (!bookablesError && bookablesData) {
      dispatchReducer({ type: 'setBookables', payload: bookablesData });
    }

    if (!specificBookablesError && specificBookablesData) {
      const getBookablesMatchingSelectedResource = (bookables) => {
        if (!bookables) return;

        return bookables.reduce((result, data) => {
          if (data.resourceId === publicData.resource.uuid) {
            for (const [date, slots] of Object.entries(data.bookables)) {
              result[date] = slots;
            }
            return result;
          }
          return result;
        }, {});
      };

      const matches = getBookablesMatchingSelectedResource(
        specificBookablesData,
      );

      dispatchReducer({
        type: 'setBookables',
        payload: matches,
      });
    }
  }, [
    bookablesData,
    bookablesError,
    specificBookablesError,
    specificBookablesData,
    publicData?.resource?.uuid,
  ]);

  // for show bookables of single resource
  useEffect(() => {
    const fetch = async () => {
      fetchProducts({
        params: {
          channelKey: channelKey,
          tslRefs: tslRefPool,
          ref: publicData?.ref,
          ...additionalParams,
        },
        body: virtualTslRefPool,
      });
    };

    if (
      !showAllBookables &&
      !productsAreControlled &&
      (tslRefPool?.length > 0 ||
        virtualTslRefPool?.length > 0 ||
        publicData?.ref)
    ) {
      fetch();
    }
  }, [
    publicData,
    productsAreControlled,
    fetchProducts,
    channelKey,
    additionalParams,
    showAllBookables,
    tslRefPool,
    virtualTslRefPool,
  ]);

  // for showAllBookables
  useEffect(() => {
    const fetch = async () => {
      // tslrefs and virtualTslRefs are not yet supported for the view all options
      fetchProducts({
        params: {
          ref: refPool,
          channelKey: channelKey,
          ...additionalParams,
        },
      });
    };

    if (showAllBookables && !productsAreControlled) {
      fetch();
    }
  }, [
    productsAreControlled,
    fetchProducts,
    channelKey,
    additionalParams,
    showAllBookables,
    refPool,
  ]);

  // Products belong to the selected resource, so drop them the moment that
  // changes. Without this they stay in place until the refetch resolves, and
  // anything comparing the held product against them — the mismatch
  // reconciliation below — judges it against the *previous* resource's
  // catalogue. RTK serves an already-cached arg synchronously, so there is no
  // undefined window of its own to rely on.
  useEffect(() => {
    if (productsAreControlled) return;
    dispatchReducer({ type: calendarActions.resetProducts });
  }, [publicData?.ref, productsAreControlled]);

  // we dispatch in a different useEffect so that the results of automatic refetches
  // can be properly dispatched. Otherwise, after the initial request, we'd never update the interface again
  useEffect(() => {
    if (!productsAreControlled && !productsError) {
      dispatchReducer({
        type: calendarActions.setProducts,
        payload: productsData?.products,
      });
    }
  }, [
    productsData,
    bookablesAreControlled,
    productsAreControlled,
    productsError,
  ]);

  useEffect(() => {
    // `product` can transiently become undefined (e.g. switching to a resource
    // whose product list is empty resets it) while a booking dialog is open, so
    // guard it here — the body dereferences product.uuid.
    if (clickedEvent && product) {
      reserveAppointment({
        channelOrResourceId: publicData?.ref,
        body: {
          timeslotUuid: clickedEvent?.timeslot_uuid,
          appointmentUuid: clickedEvent?.appointment_uuid,
          productUuid: product.uuid,
          from: clickedEvent.start
            .set({ milliseconds: 0 })
            .toUTC(0, {
              keepLocalTime: true,
            })
            .toISO({
              suppressMilliseconds: true,
            }),
          to: clickedEvent.end
            .set({ milliseconds: 0 })
            .toUTC(0, {
              keepLocalTime: true,
            })
            .toISO({
              suppressMilliseconds: true,
            }),
        },
        params: {
          channelKey: channelKey,
          ...additionalParams,
        },
      });
    }
  }, [
    channelKey,
    clickedEvent,
    product?.uuid,
    publicData?.ref,
    reserveAppointment,
    additionalParams,
  ]);

  useMemo(() => {
    if (reservationFailed) {
      handleOpenErrorDialog();
      handleCloseBookingPage();
    }
  }, [handleCloseBookingPage, reservationFailed]);

  // Restore the "all" view once the booking flow is over — armed by
  // handleOpenBookingPage whenever a slot was taken from that view.
  // Fires when both booking page and confirmation page are closed.
  useEffect(() => {
    if (
      shouldRestoreAllView &&
      !isBookingPageOpen &&
      !isConfirmationPageOpen &&
      !clickedEvent
    ) {
      const allOptionData = publicDataMap?.['all-option'];
      if (allOptionData) {
        dispatch(selectPublicData(allOptionData));
      }
      setShouldRestoreAllView(false);
    }
  }, [
    shouldRestoreAllView,
    isBookingPageOpen,
    isConfirmationPageOpen,
    clickedEvent,
    publicDataMap,
    dispatch,
  ]);

  // Auto-open cancel dialog when cancelable bookable is fetched via dedicated query
  // This runs before product selection, allowing immediate cancel flow
  useMemo(() => {
    if (
      !isCancelPageOpen &&
      !wasCancelPageOpen &&
      cancelableBookableData &&
      authToken
    ) {
      const cancelableBookable = findCancelableBookable(cancelableBookableData);

      if (cancelableBookable) {
        setClickedCancelableEvent(preProcessBookbale(cancelableBookable));
        setCancelPageOpen(true);
        setWasCancelPageOpen(true);
      }
    }
  }, [
    isCancelPageOpen,
    wasCancelPageOpen,
    cancelableBookableData,
    authToken,
    findCancelableBookable,
    preProcessBookbale,
  ]);

  // Fallback: Auto-open cancel dialog from bookables response (legacy flow)
  useMemo(() => {
    if (
      !isCancelPageOpen &&
      !wasCancelPageOpen &&
      bookables &&
      cancelableAppointmentUuid &&
      authToken
    ) {
      const cancelableBookable = findCancelableBookable(
        bookables,
        cancelableAppointmentUuid,
      );

      if (cancelableBookable) {
        setClickedCancelableEvent(preProcessBookbale(cancelableBookable));
        setCancelPageOpen(true);
        setWasCancelPageOpen(true);
      }
    }
  }, [
    isCancelPageOpen,
    wasCancelPageOpen,
    bookables,
    cancelableAppointmentUuid,
    authToken,
    findCancelableBookable,
    preProcessBookbale,
  ]);

  useMemo(() => {
    if (productsError && fetchingProductsFailed) {
      fetchingProductsFailed(productsError);
    }
  }, [productsError, fetchingProductsFailed]);

  useMemo(() => {
    if (bookablesError && fetchingBookablesFailed) {
      fetchingBookablesFailed(bookablesError);
    }
  }, [bookablesError, fetchingBookablesFailed]);

  useMemo(() => {
    if (cancelableBookableError && fetchingCancelableBookableFailed) {
      fetchingCancelableBookableFailed(cancelableBookableError);
    }
  }, [cancelableBookableError, fetchingCancelableBookableFailed]);

  useMemo(() => {
    if (products && fetchingProductsSucceeded) {
      fetchingProductsSucceeded({ ...products });
    }
  }, [products, fetchingProductsSucceeded]);

  useMemo(() => {
    if (bookables && fetchingBookablesSucceeded) {
      fetchingBookablesSucceeded({ ...bookables });
    }
  }, [bookables, fetchingBookablesSucceeded]);

  useMemo(() => {
    if (publicData && fetchingPublicDataSucceeded) {
      fetchingProductsSucceeded({ ...publicData });
    }
  }, [publicData, fetchingPublicDataSucceeded, fetchingProductsSucceeded]);

  // todo: check wether productRef is given and auto select if so
  useMemo(() => {
    if (!product && products?.length === 1) {
      dispatch(selectProduct(products[0]));
    } else if (!product && !isProductSelectionOpen && products?.length > 1) {
      handleOpenProductPage();
    }
  }, [
    product,
    products,
    isProductSelectionOpen,
    dispatch,
    handleOpenProductPage,
  ]);

  // Deliberately an effect, not the render-phase useMemo this used to be: the
  // redux dispatch below changes useSelector's store snapshot mid-render, which
  // makes React throw away every render-phase setState queued alongside it. The
  // old code only got away with it because enqueueSnackbar was an external side
  // effect — opening the product dialog is React state and got swallowed.
  useEffect(() => {
    if (bookingFlowActive) return;
    if (!product || !products) return;

    const match = products.find((p) => p.uuid === product.uuid);
    if (match) return;

    // Only reset to a valid replacement; never dispatch
    // selectProduct(undefined) (an empty product list for the switched-to
    // resource) — that nulls `product` and crashed the reserve effect.
    if (products.length > 0) {
      dispatch(selectProduct(products[0]));
    }
    // Let the customer re-pick instead of silently swapping their product.
    // The isProductSelectionOpen guard matters for the empty-list case, where
    // no replacement is dispatched and this would otherwise re-fire the
    // openedProductSelection callback on every pass.
    setProductMismatch(true);
    if (!isProductSelectionOpen) {
      handleOpenProductPage();
    }
  }, [
    product,
    products,
    dispatch,
    isProductSelectionOpen,
    handleOpenProductPage,
    bookingFlowActive,
  ]);

  const hasCompletedInitialLoadRef = useRef(false);

  const bookablesQueryStarted =
    (!isLoadingSpecificBookables &&
      !isFetchingSpecificBookablesUninitialized) ||
    (!isLoadingBookables && !isFetchingBookablesUninitialized);

  // Products has settled with nothing to show — empty list or fetch error.
  // Drives both the loading-latch terminal signal (no bookables fetch will ever
  // fire) and DetailsView's ProductCard hiding itself.
  const noProductsAvailable =
    !productsAreControlled &&
    (Boolean(productsError) || products?.length === 0);

  const bookablesPathSettled = bookablesQueryStarted || noProductsAvailable;

  // One-way latch: once true, stays true. Uses useMemo to avoid bare render-phase ref mutation.
  const hasCompletedInitialLoad = useMemo(() => {
    if (hasCompletedInitialLoadRef.current) return true;
    if (!isWaitingForConf && bookablesPathSettled) {
      hasCompletedInitialLoadRef.current = true;
      return true;
    }
    return false;
  }, [isWaitingForConf, bookablesPathSettled]);

  const isFetching = useMemo(() => {
    // During initial load: stay in loading state continuously to prevent intermediate flashes
    if (!hasCompletedInitialLoad) {
      return true;
    }
    // After initial load: normal per-query tracking
    return (
      isFetchingProducts ||
      isFetchingBookables ||
      isIdentifyingCustomer ||
      isFetchingSpecificBookables ||
      isFetchingCancelableBookable ||
      isWaitingForConf
    );
  }, [
    hasCompletedInitialLoad,
    isFetchingBookables,
    isFetchingCancelableBookable,
    isFetchingProducts,
    isFetchingSpecificBookables,
    isIdentifyingCustomer,
    isWaitingForConf,
  ]);

  const inDetailsMode = useMemo(
    () => calendarFrontend?.includes('details'),
    [calendarFrontend],
  );

  const bookingPageState = useMemo(
    () => ({
      pageContainerProps: {
        onClose: handleCloseBookingPage,
        identifiedOpen:
          isBookingPageOpen && !identificationFailed && !isIdentifyingCustomer,
        unknownOpen:
          isBookingPageOpen && identificationFailed && !isIdentifyingCustomer,
        constrainDialogsToContainer,
      },
      timeslot: (() => {
        // this is kinda hacky.
        // after successful reservation we have created an appointment with a new timeslot
        // we need to excahnge the uuid here so we book the customer to the new appointment instead the underlying availability
        // (which wouldn't work anyway bc of collision)
        if (
          clickedEvent &&
          clickedEvent.uuid !== reservation?.participation?.timeslot_uuid
        ) {
          clickedEvent.uuid = reservation?.participation?.timeslot_uuid;
        }
        return clickedEvent;
      })(),
      productName: clickedEvent?.product_name
        ? clickedEvent.product_name
        : product?.name,
      resourceName: publicData?.resource?.name,
      placeholderId: reservation?.participation?.customer_uuid,
      bookingProcess: publicData?.channel?.bookingProcess,
      customerData: customerData,
      onBookingSuccessfull: ({ timeslot, response, data }) => {
        resetReservation(); //<- reset reservation result - we otherwise delete the booking we just created
        handleCloseBookingPage();
        handleOpenConfirmationPage();
        setBookingResponse(response?.data?.createdAppointment);
        setBookedEvent(timeslot);
        setInputData(data);
      },
      onBookingFailed: ({ response, data }) => {
        if (response?.error?.data?.errors[0]?.errorCode === '300')
          handleOpenLoginPage(data.email);
      },
    }),
    [
      clickedEvent,
      constrainDialogsToContainer,
      customerData,
      handleCloseBookingPage,
      handleOpenConfirmationPage,
      identificationFailed,
      isBookingPageOpen,
      isIdentifyingCustomer,
      product?.name,
      publicData?.channel?.bookingProcess,
      publicData?.resource?.name,
      reservation?.participation?.customer_uuid,
      reservation?.participation?.timeslot_uuid,
      resetReservation,
    ],
  );

  const productPageState = useMemo(() => {
    return {
      pageContainerProps: {
        // Never over the booking form. LoginPage may stack there (the
        // account-exists flow); a forced product re-pick may not.
        open: isProductSelectionOpen && !isCancelPageOpen && !isBookingPageOpen,
        onClose: handleCloseProductPage,
        constrainDialogsToContainer,
        // an empty product list leaves nothing to pick, so don't trap the user
        requireSelection: productMismatch && products?.length > 0,
      },
      products,
      selectedProduct: product,
      unsupportedProductNotice: productMismatch,
      selectProduct: (product) => {
        dispatch(selectProduct(product));
      },
    };
  }, [
    constrainDialogsToContainer,
    dispatch,
    handleCloseProductPage,
    isBookingPageOpen,
    isCancelPageOpen,
    isProductSelectionOpen,
    product,
    productMismatch,
    products,
  ]);

  const cancelPageState = useMemo(
    () => ({
      pageContainerProps: {
        open: isCancelPageOpen,
        onClose: handleCloseCancelPage,
        constrainDialogsToContainer,
        allowCloseOnCancel,
      },
      timeslot: clickedCancelableEvent,
      productName: clickedCancelableEvent?.product_name
        ? clickedCancelableEvent.product_name
        : product?.name,
      resourceName: publicData?.resource?.name,
      onCancelSuccess: handleCancelSuccess,
    }),
    [
      allowCloseOnCancel,
      clickedCancelableEvent,
      constrainDialogsToContainer,
      handleCancelSuccess,
      handleCloseCancelPage,
      isCancelPageOpen,
      product?.name,
      publicData?.resource?.name,
    ],
  );

  const confirmationPageState = useMemo(
    () => ({
      pageContainerProps: {
        open: isConfirmationPageOpen,
        onClose: handleCloseConfirmationPage,
        allowCloseOnBooking: allowCloseOnBooking,
        constrainDialogsToContainer,
      },
      timeslot: bookedEvent,
      productName: bookedEvent?.product_name
        ? bookedEvent.product_name
        : product?.name,
      contactName: publicData?.contact?.name,
      contactEmail: publicData?.contact?.email,
      contactPhone: publicData?.contact?.mobile
        ? publicData?.contact?.mobile
        : publicData?.contact?.phone,
      productDescription: product?.detailsPost,
      resourceName: publicData?.resource?.name,
      resourceDescription: publicData?.resource?.description,
      providerName: publicData?.provider?.isThemingAllowed
        ? publicData?.provider?.name
        : undefined,
      customerMail: customerData ? customerData.email : inputData?.email,
      contactChannel: bookingResponse?.contact_channel,
      cancelLink: bookingResponse?.cancelLink,
      bookingProcess: publicData?.channel?.bookingProcess,
    }),
    [
      allowCloseOnBooking,
      bookedEvent,
      bookingResponse?.cancelLink,
      bookingResponse?.contact_channel,
      constrainDialogsToContainer,
      customerData,
      handleCloseConfirmationPage,
      inputData?.email,
      isConfirmationPageOpen,
      product?.detailsPost,
      product?.name,
      publicData?.channel?.bookingProcess,
      publicData?.contact?.email,
      publicData?.contact?.mobile,
      publicData?.contact?.name,
      publicData?.contact?.phone,
      publicData?.provider?.isThemingAllowed,
      publicData?.provider?.name,
      publicData?.resource?.description,
      publicData?.resource?.name,
    ],
  );

  // Preprocess (deep-clone + Luxon parse) once per bookables/product change
  // rather than on every CalendarViewState recompute. The clone hands every
  // timeslot a fresh identity (and useBookableCurator mutates these clones), so
  // re-cloning on unrelated recomputes — isFetching flips, height changes — was
  // busting the curator memo and forcing CondensedView to re-render and
  // re-serialise all of its (up to several thousand) slot buttons per switch.
  const processedBookables = useMemo(
    () =>
      preProcessBookables(
        bookables,
        product,
        i18n.language,
        publicDataMap,
        bookablesAreControlled,
      ),
    [
      preProcessBookables,
      bookables,
      product,
      i18n.language,
      publicDataMap,
      bookablesAreControlled,
    ],
  );

  // Stable identity so it does not, by itself, bust the memoised Column tree in
  // CondensedView on every CalendarViewState recompute.
  const openBookingPage = useCallback(
    (event) => {
      if (event.isCancelable) {
        handleOpenCancelPage(event);
      } else {
        handleOpenBookingPage(event);
      }
    },
    [handleOpenCancelPage, handleOpenBookingPage],
  );

  const CalendarViewState = useMemo(
    () => ({
      bookables: processedBookables,
      isFetching,
      isFetchingBookables,
      isFetchingProducts,
      isFetchingSpecificBookables,
      constrainDialogsToContainer,
      isWaitingForConf,
      inDetailsMode,
      noProductsAvailable,
      product: product,
      products: products,
      resource: resource,
      publicDataMap: publicDataMap,
      publicData: publicData,
      height: height - 36, // Subtract footer height
      openResourcePage: suppressResourcePage
        ? undefined
        : handleOpenResourceSelectionPage,
      openProductPage: handleOpenProductPage,
      isViewportSmall: isSmallScreen,
      openBookingPage,
    }),
    [
      processedBookables,
      product,
      isFetching,
      isFetchingBookables,
      isFetchingProducts,
      isFetchingSpecificBookables,
      constrainDialogsToContainer,
      isWaitingForConf,
      inDetailsMode,
      isSmallScreen,
      noProductsAvailable,
      products,
      resource,
      publicDataMap,
      publicData,
      height,
      suppressResourcePage,
      handleOpenResourceSelectionPage,
      handleOpenProductPage,
      openBookingPage,
    ],
  );

  const LoginPageState = useMemo(
    () => ({
      pageContainerProps: {
        open: isLoginPageOpen,
        onClose: handleCloseLoginPage,
        constrainDialogsToContainer,
      },
      userName: loginPagePrefilledNamed,
      doOnSuccess: handleCloseLoginPage,
    }),
    [constrainDialogsToContainer, isLoginPageOpen, loginPagePrefilledNamed],
  );

  const ErrorState = useMemo(
    () => ({
      pageContainerProps: {
        open: isErrorDialogOpen,
        onClose: handleCloseErrorDialog,
        constrainDialogsToContainer,
      },
    }),
    [constrainDialogsToContainer, handleCloseErrorDialog, isErrorDialogOpen],
  );

  /***********************
   * RENDERING
   ***********************/

  const contextProviders = (
    <ProductPageContext.Provider value={productPageState}>
      <CustomerFormContext.Provider value={bookingPageState}>
        <CancelPageContext.Provider value={cancelPageState}>
          <ConfirmationPageContext.Provider value={confirmationPageState}>
            <ErrorContext.Provider value={ErrorState}>
              <LoginPageContext.Provider value={LoginPageState}>
                <CalendarViewContext.Provider value={CalendarViewState}>
                  {children}
                </CalendarViewContext.Provider>
              </LoginPageContext.Provider>
            </ErrorContext.Provider>
          </ConfirmationPageContext.Provider>
        </CancelPageContext.Provider>
      </CustomerFormContext.Provider>
    </ProductPageContext.Provider>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid
        container
        wrap="nowrap"
        sx={{ flexGrow: 1, boxSizing: 'border-box', overflow: 'hidden' }}
      >
        {!suppressResourcePage && (
          <ResourcePage
            mobileOpen={isResourceSelectionOpen}
            mobileOnClose={handleCloseResourceSelectionPage}
            publicDataMap={publicDataMap}
            publicData={publicData}
            shouldDisplay={
              publicDataMap &&
              Object.keys(publicDataMap).length > 1 &&
              !forceResourceSelectorDialog
            }
            height={height - 36}
          >
            {isSmallScreen && (
              <ResourceSelector
                onResourceSelected={() => {
                  handleCloseResourceSelectionPage();
                }}
                height={height - 40}
                HeadlineProps={{
                  variant: 'h2',
                }}
                ListItemProps={{
                  sx: {
                    mx: 'auto',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: (theme) => theme.palette.divider,
                    marginBottom: '16px',
                    minHeight: '58px',
                  },
                }}
                ListItemButtonProps={{
                  sx: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                  },
                }}
              />
            )}
            {!isSmallScreen && (
              <ResourceSelector
                onResourceSelected={() => {
                  handleCloseResourceSelectionPage();
                }}
                height={height - 36}
                ListProps={{ sx: { maxWidth: '240px', pr: 2 } }}
                ListItemProps={{}}
                ListItemButtonProps={{}}
              />
            )}
          </ResourcePage>
        )}
        <Grid item xs>
          {contextProviders}
        </Grid>
      </Grid>
      {!hideTimumFooter && <Footer />}
    </Box>
  );
}
